'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'

import type { ModelsClub } from '@/api/generated-api'
import { formatLeaderboardNumber } from '@/features/leaderboard/formatLeaderboardNumber'
import { StatCard } from '@/shared/ui'
import { ClubRankIcon } from '@/ui/icons/ClubRankIcon'
import { ProfileFrameIcon } from '@/ui/icons/ProfileFrameIcon'
import { ProfileStarIcon } from '@/ui/icons/ProfileStarIcon'
import { AwardIcon } from '@/ui/icons/StatsIcons'

import S from './ClubProfileStats.module.scss'
import FanGrowthCard from './components/FanGrowthCard'
import LiveFanGrowthCard from './components/FanGrowthCard/LiveFanGrowthCard'
import TopFansCard from './components/TopFansCard'
import { formatStatValue } from './formatStatValue'

interface ClubProfileStatsProps {
  club: ModelsClub
  clubRank?: number
  /** The current balance — a fallback for an API without the total. */
  balancePts?: number
  /** BAT-1205: the Arena Points ever earned (the Overview card). */
  totalEarnedPts?: number
  fanViews?: number
  challengesCompleted?: number
  onViewLeaderboard?: () => void
  /** BAT-1106: athlete Overview — Athlete Rank, Arena Points, Tasks
   * completed and a live Fan growth. */
  isAthlete?: boolean
}

const ClubProfileStats = ({
  club,
  clubRank,
  balancePts,
  totalEarnedPts,
  fanViews,
  challengesCompleted,
  onViewLeaderboard,
  isAthlete = false
}: ClubProfileStatsProps) => {
  const t = useTranslations('club.stats')
  const fanCount = club.fan_count ?? 0

  return (
    <div className={S.wrapper}>
      <div className={S.stats}>
        <div className={S.leftBlock}>
          <span className={S.leftLabel}>
            {isAthlete ? t('athleteRank') : t('globalRank')}
          </span>
          <div className={S.rankValueWrap}>
            <span className={S.leftValue}>
              {clubRank != null ? `#${clubRank}` : '—'}
            </span>
          </div>
          <Image
            src="/assets/club-profile/club-profile-club-rank-fire.png"
            alt=""
            width={320}
            height={320}
            className={S.rankFire}
            priority={false}
          />
        </div>

        <div className={S.middleColumn}>
          <div className={S.earnedApCard}>
            {/* BAT-1205: Overview shows the Arena Points ever earned, to
                everyone; the current balance lives on the Monetization tab. */}
            <StatCard
              label={t('arenaPoints')}
              value={formatStatValue(totalEarnedPts ?? balancePts)}
              icon={<ProfileStarIcon />}
              hasGridBackground
            />
          </div>
          <div className={S.fansCard}>
            <StatCard
              label={t('fans')}
              value={formatLeaderboardNumber(fanCount)}
              icon={<ProfileFrameIcon />}
              hasGridBackground
            />
          </div>
        </div>

        <div className={S.rightColumn}>
          <div className={S.challengesCard}>
            <StatCard
              label={isAthlete ? t('tasksCompleted') : t('challengesCompleted')}
              value={formatStatValue(challengesCompleted)}
              icon={<AwardIcon />}
              hasGridBackground
            />
          </div>
          <div className={S.fansViewsCard}>
            <StatCard
              label={t('fansViews')}
              value={formatStatValue(fanViews)}
              icon={<ClubRankIcon />}
              hasGridBackground
            />
          </div>
        </div>
      </div>

      <div className={S.bottomRow}>
        <TopFansCard
          clubId={club.id}
          onViewLeaderboard={onViewLeaderboard}
          linkLabel={isAthlete ? t('fansLeaderboard') : undefined}
        />
        {isAthlete ? (
          <LiveFanGrowthCard clubId={club.id ?? ''} />
        ) : (
          <FanGrowthCard />
        )}
      </div>
    </div>
  )
}

export default ClubProfileStats
